<template>
  <div>
    <div class="collaborator-title-wrapper">
      <FriedH4 class="collaborator-title">{{
        `${t('dr_settings_collaborators_title')} (${allSelectedCollaborators.length})`
      }}</FriedH4>
      <FriedInfoPopper
        :icon-size="IconSize.Small"
        icon="info-circle"
        trigger-data-external="dr-collaborator-info-popper-trigger"
        >{{ t('dr_info_popper_collaborators') }}</FriedInfoPopper
      >
    </div>
    <FriedParagraph>{{ t('dr_invite_your_colleagues_to_help_you') }}</FriedParagraph>
    <FriedDropdown
      class="collaboration-input collaboration-select"
      mobile-full-screen
      same-width-as-ref
      :close-on-action="false"
      :mobile-padding="false"
      :open="dropdownOpen"
      @update:open="dropdownOpen = $event"
    >
      <template #trigger>
        <FriedInput
          v-model="query"
          data-external="collaborators-filter-search-input"
          class="search-collaborators-input"
          :label="t('collaborators')"
        >
          <template #append>
            <FriedIcon :class="['chevron-icon', { expanded: dropdownOpen }]" icon="chevron-down" />
          </template>
        </FriedInput>
      </template>
      <template #content>
        <FriedDropdownItem
          v-for="(user, index) in filteredUsers"
          :key="user.id"
          @click="selectCollaborator(user)"
        >
          <div
            :class="['collaborator-option', { 'last-option': index === filteredUsers.length - 1 }]"
          >
            <FriedCheckbox
              v-tooltip="{
                content: isCollaboratorAlreadyAdded(user.id) && t('dr_collaborator_already_added'),
                placement: Placement.TopStart,
                delay: { show: 300, hide: 100 },
                distance: 8,
              }"
              :disabled="isCollaboratorAlreadyAdded(user.id)"
              :data-external="`collaborator-checkbox-${user.id}`"
              :model-value="isSelected(user.id)"
            />
            <FriedParagraph>{{ user.fullName }}</FriedParagraph>
          </div>
        </FriedDropdownItem>
        <FriedDropdownItem v-if="filteredUsers.length === 0">
          <FriedParagraph> {{ t('no_matching_names') }} </FriedParagraph>
        </FriedDropdownItem>
        <div class="add-collaborator-button-container">
          <FriedButton
            :disabled="collaboratorsToAdd.length === 0"
            data-external="add-collaborators-button"
            @click="addCollaborators"
          >
            {{ t('dr_add_to_room') }}
          </FriedButton>
        </div>
      </template>
    </FriedDropdown>
    <div class="collaboration-list">
      <CollaboratorListItem
        v-for="collaborator in addedCollaborators"
        :key="collaborator.id"
        :user="collaborator"
        can-edit
        @remove="remove"
      />
    </div>
  </div>
</template>
<script lang="ts">
import type { Ref } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import CollaboratorListItem from '@getaccept/lib-shared/src/dealroom/components/CollaboratorListItem.vue';
import { UserStatus } from '@getaccept/lib-shared/src/users/types/user-status';
import { storeToRefs } from 'pinia';
import { IconSize, Placement } from '@getaccept/fried-tofu';
import { useUsers } from '../users/composables/users.composable';
import type { User } from '../user/types/user';
import { useUserStore } from '../user/store/user.store';

export default defineComponent({
  components: {
    CollaboratorListItem,
  },
  emits: ['update-collaborators'],
  setup(_, { emit }) {
    const userStore = useUserStore();
    const { user: currentUser } = storeToRefs(userStore);
    const addedCollaborators: Ref<User[]> = ref([]);
    const query = ref('');
    const dropdownOpen = ref(false);

    const { data: users } = useUsers();

    const activeUsers = computed(() =>
      [...users.value]
        .filter(
          user =>
            user?.userProfile?.userStatus === UserStatus.Active || currentUser.value.id !== user.id
        )
        .filter((user, index, self) => self.findIndex(u => u.id === user.id) === index)
        .map(user => ({ ...user, fullName: user.fullName || user.email }))
    );

    const filteredUsers = computed(() =>
      activeUsers.value?.filter(user =>
        user.fullName?.toLowerCase().includes(query.value.toLowerCase())
      )
    );

    const isCollaboratorAlreadyAdded = (id: string) =>
      addedCollaborators.value.some(collaborator => collaborator.id === id);

    const addCollaborators = () => {
      emit('update-collaborators', allSelectedCollaborators.value);
      addedCollaborators.value = allSelectedCollaborators.value;
      collaboratorsToAdd.value = [];
      dropdownOpen.value = false;
      query.value = '';
    };

    const remove = (id: string) => {
      addedCollaborators.value = addedCollaborators.value.filter(
        collaborator => collaborator.id !== id
      );
      emit('update-collaborators', addedCollaborators.value);
    };

    const allSelectedCollaborators = computed(() => [
      ...addedCollaborators.value,
      ...collaboratorsToAdd.value,
    ]);

    const collaboratorsToAdd: Ref<User[]> = ref([]);

    const selectCollaborator = (user: User) => {
      collaboratorsToAdd.value = isSelected(user.id)
        ? collaboratorsToAdd.value.filter(collaborator => collaborator.id !== user.id)
        : [...collaboratorsToAdd.value, user];
    };

    const isSelected = (id: string) =>
      allSelectedCollaborators.value.some(collaborator => collaborator.id === id);

    return {
      query,
      filteredUsers,
      dropdownOpen,
      t,
      remove,
      users,
      selectCollaborator,
      addedCollaborators,
      allSelectedCollaborators,
      addCollaborators,
      collaboratorsToAdd,
      isCollaboratorAlreadyAdded,
      Placement,
      isSelected,
      IconSize,
    };
  },
});
</script>
<style lang="scss" scoped>
.collaboration-input {
  width: 100%;
  margin: var(--spacing-50) 0;
}

.collaboration-select {
  :deep(.dropdown-content) {
    max-height: 350px;
  }
}

.collaborator-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-100);
}

.search-collaborators-input {
  width: 100%;
}

.chevron-icon {
  transition: all 0.2s ease-in-out;
  margin-right: var(--spacing-50);

  &.expanded {
    transform: rotate(180deg);
  }
}

.add-collaborator-button-container {
  position: fixed;
  bottom: 0;
  width: calc(100% - var(--spacing-100));
  background-color: var(--white);
  padding: var(--spacing-50);

  .button {
    width: 100%;
  }
}

.last-option {
  margin-bottom: 3rem;
}

.collaborator-title-wrapper {
  display: flex;
  align-items: center;
  margin-top: var(--spacing-100);
  gap: var(--spacing-50);
}

.collaboration-list {
  max-height: 340px;
  overflow-y: auto;
}
</style>
